import axios from 'axios';
import { AlertCircle } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Calendar } from '../../../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../../../components/ui/popover';
import { Checkbox } from '../../../components/ui/checkbox';
import { useEffect, useState } from 'react';
import { StepCard } from '../StepCard';
import { CaretDownIcon } from '@phosphor-icons/react';

export function PersonalDetails({ onNext, onPrevious, data, onDataChange }) {
  const [formData, setFormData] = useState({
    name: data?.name || '',
    mobileNumber: data?.mobileNumber || '',
    isWhatsAppNumber: data?.isWhatsAppNumber || false,
    dob: data?.dob || '',
  });

  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [open, setOpen] = useState(false);

  const formatDateToYYYYMMDD = (dateInput) => {
    if (!dateInput) return '';

    let date;
    if (dateInput instanceof Date) {
      date = dateInput;
    } else {
      date = new Date(dateInput);
    }

    if (isNaN(date.getTime())) {
      console.warn('Invalid date provided to formatDateToYYYYMMDD:', dateInput);
      return '';
    }

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const parseLocalDate = (yyyyMMdd) => {
    if (!yyyyMMdd) return undefined;
    const [year, month, day] = yyyyMMdd.split('-').map(Number);
    return new Date(year, month - 1, day);
  };

  useEffect(() => {
    const userProfile = JSON.parse(localStorage.getItem('userProfile') || '{}');
    if (userProfile.name) {
      setFormData((prev) => ({
        ...prev,
        name: userProfile.name,
        mobileNumber: userProfile.mobileNumber?.slice(-10) || '',
        isWhatsAppNumber: userProfile.isWhatsAppNumber || false,
        dob: userProfile.dob ? formatDateToYYYYMMDD(userProfile.dob) : '',
      }));
    }
  }, []);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'This field is required';
    } else if (!/^[a-zA-Z\s]+$/.test(formData.name)) {
      newErrors.name = 'Name can only contain alphabets';
    } else if (formData.name.trim().length < 3) {
      newErrors.name = 'Name should have at least 3 letters';
    } else if (formData.name.trim().length > 70) {
      newErrors.name = 'Name should have at most 70 characters';
    }

    if (!formData.mobileNumber.trim()) {
      newErrors.mobileNumber = 'This field is required';
    } else if (!/^\d{10}$/.test(formData.mobileNumber)) {
      newErrors.mobileNumber = 'Please enter a valid 10-digit Indian mobile number';
    }

    if (!formData.dob.trim()) {
      newErrors.dob = 'This field is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    try {
      const updateData = {
        name: formData.name.trim(),
        mobileNumber: `+91${formData.mobileNumber}`,
        isWhatsAppNumber: formData.isWhatsAppNumber,
        dob: formData.dob,
      };

      const response = await axios.patch(`/api/user/profile/`, updateData);

      if (response.status === 200) {
        localStorage.setItem('userProfile', JSON.stringify(response.data.data.user));
        onDataChange(updateData);
        onNext();
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      setErrors({ submit: 'Failed to save data. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));

    const newErrors = { ...errors };

    if (field === 'name') {
      if (!value.trim()) {
        delete newErrors.name;
      } else if (!/^[a-zA-Z\s]+$/.test(value)) {
        newErrors.name = 'Name can only contain alphabets';
      } else if (value.trim().length < 3) {
        newErrors.name = 'Name should have at least 3 letters';
      } else if (value.trim().length > 70) {
        newErrors.name = 'Name should have at most 70 characters';
      } else {
        delete newErrors.name;
      }
    }

    if (field === 'mobileNumber') {
      if (!value.trim()) {
        delete newErrors.mobileNumber;
      } else if (!/^\d{10}$/.test(value)) {
        newErrors.mobileNumber = 'Please enter a valid 10-digit Indian mobile number';
      } else {
        delete newErrors.mobileNumber;
      }
    }

    if (field === 'dob') {
      if (!value.trim()) {
        delete newErrors.dob;
      } else {
        delete newErrors.dob;
      }
    }

    setErrors(newErrors);
  };

  return (
    <StepCard>
      <div className="space-y-3">
        <div>
          <h2 className="text-2xl sm:text-3xl font-bold text-[var(--q3-neutral-default)] mb-2">Personal Details</h2>
          <p className="text-[var(--q3-neutral-light)] text-sm">
            Please enter your name, phone number, and date of birth to continue
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-2">
          <div className="space-y-2">
            <div>
              <label className="block text-sm font-medium text-[var(--q3-neutral-default)] mb-2">Full Name</label>
              <Input
                type="text"
                placeholder="Enter your full name"
                value={formData.name}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^a-zA-Z\s]/g, '');
                  handleInputChange('name', value);
                }}
                className="h-12 rounded-full"
                required
              />
              {errors.name && (
                <div className="flex items-center gap-2 mt-1">
                  <AlertCircle className="h-4 w-4 text-[var(--q3-danger-normal)]" />
                  <span className="text-[var(--q3-danger-normal)] text-sm">{errors.name}</span>
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-[var(--q3-neutral-default)] mb-2">Date of Birth</label>
              <Popover open={open} onOpenChange={setOpen} modal={true}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    id="date"
                    className="w-full h-12 justify-between font-normal rounded-full"
                    type="button"
                  >
                    {formData.dob ? new Date(formData.dob).toLocaleDateString() : 'Select date'}
                    <CaretDownIcon />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start" side="bottom" avoidCollisions={true}>
                  <Calendar
                    mode="single"
                    selected={parseLocalDate(formData.dob)}
                    captionLayout="dropdown"
                    onSelect={(date) => {
                      if (date) {
                        const formattedDate = formatDateToYYYYMMDD(date);
                        handleInputChange('dob', formattedDate);
                        setOpen(false);
                      }
                    }}
                    disabled={(date) => date > new Date() || date < new Date('1970-01-01')}
                    defaultMonth={parseLocalDate(formData.dob) || new Date('2003-01-01')}
                    initialFocus={!!parseLocalDate(formData.dob)}
                  />
                </PopoverContent>
              </Popover>
              {errors.dob && (
                <div className="flex items-center gap-2 mt-1">
                  <AlertCircle className="h-4 w-4 text-[var(--q3-danger-normal)]" />
                  <span className="text-[var(--q3-danger-normal)] text-sm">{errors.dob}</span>
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-[var(--q3-neutral-default)] mb-2">Phone Number</label>
              <div className="flex gap-2">
                <div className="flex items-center px-3 py-2 border border-[var(--q3-stroke-normal)] rounded-full bg-[var(--q3-surface-default)] min-w-[80px]">
                  <span className="text-sm">🇮🇳 +91</span>
                </div>
                <Input
                  type="tel"
                  placeholder="Enter your phone number"
                  value={formData.mobileNumber}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                    handleInputChange('mobileNumber', value);
                  }}
                  className="flex-1 h-12 rounded-full"
                  maxLength={10}
                  required
                />
              </div>
              {errors.mobileNumber && (
                <div className="flex items-center gap-2 mt-1">
                  <AlertCircle className="h-4 w-4 text-[var(--q3-danger-normal)]" />
                  <span className="text-[var(--q3-danger-normal)] text-sm">{errors.mobileNumber}</span>
                </div>
              )}
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="whatsapp"
                checked={formData.isWhatsAppNumber}
                onCheckedChange={(checked) => handleInputChange('isWhatsAppNumber', checked === true)}
              />
              <label
                htmlFor="whatsapp"
                className="text-sm text-[var(--q3-neutral-default)] leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
              >
                I have WhatsApp on this number
              </label>
            </div>
          </div>

          {errors.submit && <div className="text-[var(--q3-danger-normal)] text-sm text-center">{errors.submit}</div>}

          <div className="flex flex-col sm:flex-row pt-4 gap-4 sm:gap-x-4">
            <Button
              type="button"
              variant="secondary"
              onClick={onPrevious}
              className="w-full sm:flex-shrink-0 sm:flex-grow-0 sm:basis-1/3 px-4 sm:px-8 rounded-full bg-[var(--q3-surface-default)] hover:bg-[var(--q3-surface-default)]/90 text-[var(--q3-neutral-default)] disabled:opacity-50 border border-[var(--q3-stroke-normal)]"
              disabled
            >
              Previous
            </Button>
            <Button
              type="submit"
              className="w-full sm:flex-shrink-0 sm:flex-grow sm:basis-2/3 bg-[var(--q3-accent-normal)] hover:bg-[var(--q3-accent-normal)] text-[var(--q3-neutral-default_inverted)] disabled:opacity-50 px-4 sm:px-8 rounded-full"
              disabled={isLoading}
            >
              {isLoading ? 'Saving...' : 'Save & Next'}
            </Button>
          </div>
        </form>
      </div>
    </StepCard>
  );
}
